// src/cockpit4/components/CrossSellRecommender.tsx
// ============================================
// 智能推荐与交叉销售（客户中心 P2-功能9）- v2.0 详细改进计划
// 基于客户画像的产品推荐 / 购买关联分析 / 个性化推荐 / 效果追踪
// 数据来源：localStorage（xeaos_cross_sell）+ 关联规则引擎
// ============================================

import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Sparkles, ShoppingBag, TrendingUp, Repeat, Check } from "lucide-react";

interface CustomerOpportunity {
  id: string;
  name: string;
  currentProducts: string[];
  recommendation: string;
  reason: string;
  estValue: number;
  probability: number;
  type: "交叉销售" | "向上销售";
}

const INITIAL: CustomerOpportunity[] = [
  { id: "o1", name: "吴总监（企业服务）", currentProducts: ["CRM"], recommendation: "知识库 + 工作流自动化", reason: "购买 CRM 的企业 72% 会采购知识库", estValue: 68000, probability: 68, type: "交叉销售" },
  { id: "o2", name: "赵经理（旅行社）", currentProducts: ["营销中心"], recommendation: "媒体工作室升级", reason: "营销内容产出后需要批量生产素材", estValue: 32000, probability: 74, type: "向上销售" },
  { id: "o3", name: "李女士（连锁餐饮）", currentProducts: ["CRM", "财务"], recommendation: "经营分析 AI 决策", reason: "已有数据基础，决策层价值最大", estValue: 45000, probability: 61, type: "向上销售" },
  { id: "o4", name: "王先生（酒店采购）", currentProducts: ["项目中心"], recommendation: "Hermes 首席助理", reason: "项目密集型企业需要智能助理", estValue: 18800, probability: 55, type: "交叉销售" },
];

const KEY = "xeaos_cross_sell";

function load(): CustomerOpportunity[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return JSON.parse(raw) as CustomerOpportunity[];
  } catch { /* ignore */ }
  return INITIAL;
}

export default function CrossSellRecommender() {
  const [opportunities, setOpportunities] = useState<CustomerOpportunity[]>(load);

  const stats = useMemo(() => {
    const total = opportunities.reduce((a, o) => a + o.estValue, 0);
    const avg = opportunities.reduce((a, o) => a + o.probability, 0) / opportunities.length;
    const weighted = opportunities.reduce((a, o) => a + o.estValue * (o.probability / 100), 0);
    return { total, avg, weighted };
  }, [opportunities]);

  const markDone = (id: string) => {
    setOpportunities((prev) => prev.filter((o) => o.id !== id));
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Sparkles size={14} className="text-purple-400" />
        <h3 className="text-[12px] font-medium text-white">智能推荐与交叉销售</h3>
        <span className="text-[9px] font-mono text-gray-500 uppercase tracking-wider">P2-功能9 · Cross Sell</span>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <MetricCard label="商机总额" value={`¥${stats.total.toLocaleString()}`} color="#34D399" />
        <MetricCard label="平均赢单概率" value={`${stats.avg.toFixed(0)}%`} color="#A78BFA" />
        <MetricCard label="加权预期收入" value={`¥${stats.weighted.toLocaleString()}`} color="#38BDF8" />
      </div>

      <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
        <div className="flex items-center gap-2 mb-2">
          <ShoppingBag size={11} className="text-purple-400" />
          <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">推荐机会 · 购买关联分析</span>
        </div>
        <div className="space-y-1.5">
          {opportunities.map((o) => (
            <div key={o.id} className="rounded-md border border-white/[0.04] bg-white/[0.01] p-2.5">
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-medium text-white">{o.name}</span>
                <span className={`text-[9px] font-mono px-1.5 py-0.5 rounded ${o.type === "交叉销售" ? "bg-purple-500/15 text-purple-300" : "bg-sky-500/15 text-sky-300"}`}>{o.type}</span>
                <span className="ml-auto text-[10px] font-mono font-semibold text-emerald-400">¥{o.estValue.toLocaleString()}</span>
              </div>
              <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                <span className="text-[8px] font-mono px-1.5 py-0.5 rounded bg-white/[0.06] text-gray-400">已有：{o.currentProducts.join(" + ")}</span>
                <span className="text-[8px] font-mono px-1.5 py-0.5 rounded bg-purple-500/10 text-purple-300">推荐：{o.recommendation}</span>
              </div>
              <div className="flex items-center gap-2 mt-1.5">
                <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all" style={{ width: `${o.probability}%`, background: o.probability >= 65 ? "#34D399" : "#FBBF24" }} />
                </div>
                <span className="text-[9px] font-mono text-gray-400">{o.probability}% 赢单</span>
                <button onClick={() => markDone(o.id)} className="flex items-center gap-1 px-1.5 py-0.5 rounded border border-emerald-500/30 bg-emerald-500/10 text-[8px] font-mono text-emerald-300 hover:bg-emerald-500/20">
                  <Check size={8} /> 已转化
                </button>
              </div>
              <div className="text-[8px] font-mono text-gray-600 mt-1.5">关联依据：{o.reason}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
        <div className="flex items-center gap-2 mb-2">
          <Repeat size={11} className="text-cyan-400" />
          <span className="text-[10px] font-mono text-gray-400 uppercase tracking-wider">购买关联分析（A 购 → B 推荐）</span>
        </div>
        <div className="grid grid-cols-3 gap-2 text-[9px] font-mono">
          {[
            { color: "#34D399", text: "CRM + 财务 → 经营分析（关联度 87%）" },
            { color: "#38BDF8", text: "营销中心 + 媒体工作室 → 内容闭环（关联度 91%）" },
            { color: "#A78BFA", text: "项目中心 + Hermes → 自动化管理（关联度 76%）" },
          ].map((s, i) => (
            <div key={i} className="flex items-start gap-1.5 rounded-md border border-white/[0.04] bg-white/[0.01] p-2">
              <span className="w-1.5 h-1.5 rounded-full mt-1 flex-shrink-0" style={{ background: s.color }} />
              <span className="text-gray-400 leading-relaxed">{s.text}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function MetricCard({ label, value, color }: { label: string; value: string; color: string }) {
  return (
    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-2">
      <div className="text-[9px] font-mono text-gray-500 uppercase">{label}</div>
      <div className="text-sm font-semibold font-mono mt-0.5" style={{ color }}>{value}</div>
    </motion.div>
  );
}

